import { bulkPutRaw } from "@db/bulkPutRaw";
import {
  createSequenceMetadataDefaults,
  type GenbankReference,
  type SequenceChunk,
} from "@db/content";
import { DEFAULT_CHUNK_SIZE, getEncoder } from "@db/encoding";
import { computeContentHash } from "@db/operationLog";
import { type GenbankRecord, parseGenbank } from "@formats/genbank/parse";
import {
  type RawFeature,
  transformGenbankFeature,
} from "@formats/genbank/transform";
import type { GenbankAnalysis } from "../analyze/types";
import type { IngestResult, RawSequence } from "./types";

type SequenceMetadata = {
  description?: string;
  organism?: string;
  taxonomy?: string;
  accession?: string;
  version?: string;
  comment?: string;
  references?: GenbankReference[];
};

export function extractSequenceMetadata(
  record: GenbankRecord,
): SequenceMetadata {
  return {
    ...(record.definition && { description: record.definition }),
    ...(record.organism && { organism: record.organism }),
    ...(record.taxonomy && { taxonomy: record.taxonomy }),
    ...(record.accession && { accession: record.accession }),
    ...(record.version && { version: record.version }),
    ...(record.comment && { comment: record.comment }),
    ...(record.references.length > 0 && { references: record.references }),
  };
}

export type GenbankTransformResult = {
  sequences: RawSequence[];
  features: RawFeature[];
  result: IngestResult;
};

export function transformGenbank(
  text: string,
  analysis: GenbankAnalysis,
  fileName: string,
): GenbankTransformResult {
  const parsed = parseGenbank(text);

  const sequences: RawSequence[] = parsed.result.map((record) => ({
    id: crypto.randomUUID(),
    name: record.locus.name,
    kind: analysis.sequenceKind,
    topology:
      record.locus.topology === "circular"
        ? ("circular" as const)
        : ("linear" as const),
    sequence: record.sequence,
    ...createSequenceMetadataDefaults(),
    ...extractSequenceMetadata(record),
  }));

  const features: RawFeature[] = [];
  for (let i = 0; i < parsed.result.length; i++) {
    const record = parsed.result[i];
    const sequenceId = sequences[i].id;
    for (const genbankFeature of record.features) {
      features.push(transformGenbankFeature(genbankFeature, sequenceId));
    }
  }

  const result: IngestResult =
    sequences.length === 1
      ? {
          type: "GnomuxSequence",
          name: sequences[0].name,
          content: sequences[0].id,
        }
      : {
          type: "GnomuxSequenceCollection",
          name: fileName.replace(/\.(gb|gbk|genbank)$/i, ""),
          content: sequences.map((s) => s.id),
        };

  return { sequences, features, result };
}

export async function ingestGenbank(
  file: File,
  analysis: GenbankAnalysis,
  fileName: string,
  createdAt: Date,
  ownerId: string,
): Promise<IngestResult> {
  const text = await file.text();
  const { sequences, features, result } = transformGenbank(
    text,
    analysis,
    fileName,
  );
  const encode = getEncoder(analysis.sequenceKind);

  const allChunks: SequenceChunk[] = [];

  const hashes = await Promise.all(
    sequences.map((s) => computeContentHash(s.sequence)),
  );

  const sequenceObjects = sequences.map((seq, i) => {
    const chunkCount = Math.ceil(seq.sequence.length / DEFAULT_CHUNK_SIZE);

    for (let ci = 0; ci < Math.max(chunkCount, 1); ci++) {
      const startNt = ci * DEFAULT_CHUNK_SIZE;
      const endNt = Math.min(startNt + DEFAULT_CHUNK_SIZE, seq.sequence.length);
      const chunkStr = seq.sequence.slice(startNt, endNt);
      allChunks.push({
        sequenceId: seq.id,
        index: ci,
        data: encode(chunkStr),
      });
    }

    return {
      id: seq.id,
      name: seq.name,
      kind: seq.kind,
      length: seq.sequence.length,
      chunkSize: DEFAULT_CHUNK_SIZE,
      isLocked: false,
      createdAt,
      ownerId,
      topology: seq.topology,
      contentHash: hashes[i],
      description: seq.description ?? "",
      organism: seq.organism ?? "",
      taxonomy: seq.taxonomy ?? "",
      accession: seq.accession ?? "",
      version: seq.version ?? "",
      comment: seq.comment ?? "",
      references: seq.references ?? [],
      customMetadata: seq.customMetadata ?? [],
    };
  });

  const featureObjects = features.map((feature) => ({
    ...feature,
    createdAt,
  }));

  await bulkPutRaw("sequenceChunks", allChunks);
  await bulkPutRaw("sequences", sequenceObjects);
  await bulkPutRaw("features", featureObjects);

  return result;
}
